// frontend/src/pages/MapPage.jsx  — Mobile-optimised, memory-safe
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { GoogleMap, useJsApiLoader, Circle, Marker, InfoWindow } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { fetchPoles, logProximity } from "../services/api";
import { useGeolocation } from "../hooks/useGeolocation";
import api from "../services/api";

// ── Constants defined OUTSIDE component to prevent recreation
const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const DEFAULT_CENTER      = { lat: 7.096033, lng: 80.020300 };
const PROXIMITY_RADIUS    = parseFloat(process.env.REACT_APP_PROXIMITY_RADIUS) || 15;
const GOOGLE_MAPS_LIBS    = []; // no extra libs needed

const STATUS_COLORS = {
  Active:      "#22c55e",
  Maintenance: "#f59e0b",
  Inactive:    "#6b7280",
  Pending:     "#3b82f6",
};

// ── Push notification (only when page visible)
function sendPush(pole) {
  if (Notification.permission !== "granted") return;
  if (document.visibilityState !== "visible") return;
  try {
    new Notification("⚡ Solar Pole Nearby!", {
      body: `Within range of ${pole.name} (${pole.pole_code})`,
      icon: "/favicon.ico",
      tag:  `pole-${pole.id}`,
      renotify: false,
    });
  } catch(e) {}
}

// ── Modal — lazy rendered, only when needed
function ProfileModal({ user, onClose, onChangePassword }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:16, padding:24, width:"100%", maxWidth:380 }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#4ade80", marginBottom:16 }}>👤 Profile</div>
        {[["Name", user.full_name], ["Username", user.username], ["Role", user.role?.toUpperCase()]].map(([l,v])=>(
          <div key={l} style={{ display:"flex", justifyContent:"space-between", marginBottom:10, fontSize:13 }}>
            <span style={{ color:"#64748b" }}>{l}</span>
            <span style={{ color:"#e2e8f0", fontWeight:600 }}>{v}</span>
          </div>
        ))}
        <div style={{ display:"flex", gap:8, marginTop:16 }}>
          <button onClick={onClose} style={{ flex:1, background:"#1e2d3d", color:"#94a3b8", border:"none", borderRadius:8, padding:"9px", fontSize:12, cursor:"pointer" }}>Close</button>
          <button onClick={onChangePassword} style={{ flex:1, background:"#16a34a", color:"#fff", border:"none", borderRadius:8, padding:"9px", fontSize:12, fontWeight:700, cursor:"pointer" }}>🔒 Change Password</button>
        </div>
      </div>
    </div>
  );
}

function PasswordModal({ onClose }) {
  const [form, setForm] = useState({ current:"", newPw:"", confirm:"" });
  const [msg,  setMsg]  = useState({ text:"", ok:false });

  const save = async () => {
    setMsg({ text:"", ok:false });
    if (!form.current || !form.newPw || !form.confirm) return setMsg({ text:"All fields required", ok:false });
    if (form.newPw !== form.confirm) return setMsg({ text:"Passwords do not match", ok:false });
    if (form.newPw.length < 6) return setMsg({ text:"Min 6 characters", ok:false });
    try {
      await api.post("/auth/change-password", { currentPassword: form.current, newPassword: form.newPw });
      setMsg({ text:"✅ Password changed!", ok:true });
      setTimeout(onClose, 1500);
    } catch(err) {
      setMsg({ text: err.response?.data?.message || "Failed", ok:false });
    }
  };

  const inp = { width:"100%", background:"#0a1628", border:"1px solid #1e3a2e", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box", marginBottom:12 };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:16, padding:24, width:"100%", maxWidth:380 }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#4ade80", marginBottom:16 }}>🔒 Change Password</div>
        {[["Current",form.current,"current"],["New Password",form.newPw,"newPw"],["Confirm",form.confirm,"confirm"]].map(([label,val,key])=>(
          <input key={key} type="password" placeholder={label} value={val}
            onChange={e=>setForm(p=>({...p,[key]:e.target.value}))} style={inp} />
        ))}
        {msg.text && (
          <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:12, fontSize:12,
            background: msg.ok?"#14532d":"#450a0a", color: msg.ok?"#86efac":"#fca5a5" }}>
            {msg.text}
          </div>
        )}
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onClose} style={{ flex:1, background:"#1e2d3d", color:"#94a3b8", border:"none", borderRadius:8, padding:"9px", fontSize:12, cursor:"pointer" }}>Cancel</button>
          <button onClick={save}    style={{ flex:1, background:"#16a34a", color:"#fff",    border:"none", borderRadius:8, padding:"9px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ pole, onClose, onConfirm }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:16, padding:24, width:"100%", maxWidth:360 }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#f87171", marginBottom:12 }}>🗑 Delete Pole</div>
        <p style={{ color:"#94a3b8", fontSize:13, marginBottom:20 }}>
          Delete <strong style={{ color:"#e2e8f0" }}>{pole.name}</strong>? Cannot be undone.
        </p>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onClose}   style={{ flex:1, background:"#1e2d3d", color:"#94a3b8", border:"none", borderRadius:8, padding:"9px", fontSize:12, cursor:"pointer" }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, background:"#7f1d1d", color:"#fff",    border:"none", borderRadius:8, padding:"9px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Pole list item — memoized to prevent unnecessary re-renders
const PoleItem = ({ pole, isNear, dist, blink, onSelect, onEdit, onDelete, isAdmin }) => {
  const color = STATUS_COLORS[pole.status] || "#22c55e";
  return (
    <div style={{ borderRadius:10, padding:"10px 12px", marginBottom:8,
      border:`1px solid ${isNear ? "#22c55e" : "#1e2d3d"}`,
      background: isNear ? "rgba(34,197,94,0.08)" : "rgba(255,255,255,0.02)" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
        <span onClick={onSelect} style={{ fontWeight:700, fontSize:13, color: isNear?"#4ade80":"#e2e8f0",
          display:"flex", alignItems:"center", gap:6, flex:1, cursor:"pointer" }}>
          {isNear && (
            <span style={{ width:8, height:8, borderRadius:"50%", background:"#4ade80",
              display:"inline-block", opacity: blink ? 1 : 0.15, transition:"opacity 0.3s" }} />
          )}
          {pole.name}
        </span>
        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
          <span style={{ fontSize:10, padding:"2px 7px", borderRadius:99,
            border:`1px solid ${color}40`, background:`${color}18`, color }}>{pole.status}</span>
          {isAdmin && (
            <>
              <button onClick={onEdit}   style={{ background:"#1e3a5f", border:"1px solid #2563eb", color:"#93c5fd", borderRadius:5, padding:"2px 6px", fontSize:9, cursor:"pointer" }}>✏️</button>
              <button onClick={onDelete} style={{ background:"#450a0a", border:"1px solid #7f1d1d", color:"#f87171", borderRadius:5, padding:"2px 6px", fontSize:9, cursor:"pointer" }}>🗑</button>
            </>
          )}
        </div>
      </div>
      <div style={{ fontSize:11, color:"#64748b", lineHeight:1.5 }}>{pole.description}</div>
      {dist != null && (
        <div style={{ fontSize:11, marginTop:4, color: isNear?"#4ade80":"#475569", fontWeight: isNear?700:400 }}>
          {isNear ? `⚡ ${dist.toFixed(1)}m — YOU ARE HERE` : `${dist.toFixed(0)}m away`}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
export default function MapPage() {
  const navigate    = useNavigate();
  const currentUser = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const isAdmin     = currentUser.role === "admin";

  const [poles, setPoles]         = useState([]);
  const [selectedPole, setSelected] = useState(null);
  const [blinkOn, setBlinkOn]     = useState(false);   // single boolean instead of object
  const [modal, setModal]         = useState(null);    // "profile" | "password" | "delete"
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mapReady, setMapReady]   = useState(false);
  const [sidebarOpen, setSidebar] = useState(false); // mobile: hidden by default
  const [isMobile, setIsMobile]   = useState(window.innerWidth <= 768);

  const loggedRef   = useRef({});
  const wakeLockRef = useRef(null);
  const blinkRef    = useRef(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
    libraries: GOOGLE_MAPS_LIBS,
  });

  const { userPos, distances, nearPoles, gpsError, accuracy } = useGeolocation(poles);

  // ── Wake Lock
  useEffect(() => {
    if (!("wakeLock" in navigator)) return;
    let lock;
    navigator.wakeLock.request("screen").then(l => {
      lock = l;
      wakeLockRef.current = l;
    }).catch(()=>{});
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        navigator.wakeLock.request("screen").then(l => { lock = l; wakeLockRef.current = l; }).catch(()=>{});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      lock?.release().catch(()=>{});
    };
  }, []);

  // ── Detect mobile / resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ── Notification permission — deferred 3 seconds after load
  useEffect(() => {
    const t = setTimeout(() => {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(()=>{});
      }
    }, 3000);
    return () => clearTimeout(t);
  }, []);

  // ── Load poles
  const loadPoles = useCallback(() => {
    fetchPoles().then(r => setPoles(r.data.data)).catch(console.error);
  }, []);
  useEffect(() => { loadPoles(); }, [loadPoles]);

  // ── Blink — single boolean toggle, much lighter than per-pole object
  useEffect(() => {
    const id = setInterval(() => {
      setBlinkOn(b => !b);
    }, 700);
    return () => clearInterval(id);
  }, []);

  // ── Proximity logging + push notifications
  useEffect(() => {
    if (!userPos) return;
    poles.forEach(pole => {
      const isNear = nearPoles[pole.id];
      if (isNear && !loggedRef.current[pole.id]) {
        loggedRef.current[pole.id] = true;
        logProximity(pole.id, userPos.lat, userPos.lng, distances[pole.id]).catch(()=>{});
        sendPush(pole);
      }
      if (!isNear) loggedRef.current[pole.id] = false;
    });
  }, [nearPoles, userPos, poles, distances]);

  // ── Delete pole
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const { deletePole } = await import("../services/api");
      await deletePole(deleteTarget.id);
      loadPoles();
    } catch(e) { console.error(e); }
    setModal(null);
    setDeleteTarget(null);
  }, [deleteTarget, loadPoles]);

  const nearCount = useMemo(() =>
    Object.values(nearPoles).filter(Boolean).length,
  [nearPoles]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div style={{ display:"flex", height:"100dvh", fontFamily:"'JetBrains Mono',monospace", background:"#0a0f1a", overflow:"hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Syne:wght@700;800&display=swap');
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        .fade-in{animation:fadeIn 0.3s ease forwards}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:#1e3a2e;border-radius:4px}
        @media(max-width:768px){
          .sidebar-overlay{display:block!important}
        }
      `}</style>

      {/* ══ MOBILE TOGGLE BUTTON ══ */}
      {isMobile && (
        <button onClick={() => setSidebar(o => !o)}
          style={{ position:"fixed", top:10, left: sidebarOpen ? 278 : 10, zIndex:500,
            background:"#0d1520", border:"1px solid #22c55e", color:"#4ade80",
            borderRadius:10, width:36, height:36, fontSize:18, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow:"0 2px 12px rgba(0,0,0,0.5)", transition:"left 0.3s ease" }}>
          {sidebarOpen ? "✕" : "☰"}
        </button>
      )}

      {/* ══ DARK OVERLAY (mobile only) ══ */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebar(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:400 }} />
      )}

      {/* ══ SIDEBAR ══ */}
      <div className="sidebar" style={{
        width: 270,
        background:"#0d1520",
        borderRight:"1px solid #1a2e1a",
        display:"flex", flexDirection:"column", overflow:"hidden", flexShrink:0,
        // Mobile: slide in/out as overlay
        position: isMobile ? "fixed" : "relative",
        top: 0, left: 0, height: "100%",
        transform: isMobile ? (sidebarOpen ? "translateX(0)" : "translateX(-100%)") : "none",
        transition: "transform 0.3s ease",
        zIndex: 450,
      }}>

        {/* Header */}
        <div style={{ padding:"10px 14px", background:"linear-gradient(135deg,#0d2010,#0a1628)", borderBottom:"1px solid #1a2e1a", flexShrink:0 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:16, fontWeight:800, color:"#4ade80" }}>⚡ CEB Solar Poles</div>
          <div style={{ fontSize:9, color:"#4b5563", letterSpacing:"0.12em", marginTop:1 }}>FIELD SUPERVISORY SYSTEM</div>

          {/* User card */}
          <div style={{ marginTop:8, padding:"8px 10px", background:"rgba(255,255,255,0.03)", borderRadius:10, border:"1px solid #1e3a2e" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"#e2e8f0" }}>👤 {currentUser.full_name || currentUser.username}</div>
                <div style={{ fontSize:9, color:"#4b5563", marginTop:1, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                  {currentUser.role === "admin" ? "🔑 Admin" : currentUser.role === "supervisor" ? "🦺 Supervisor" : "👁 Viewer"}
                </div>
              </div>
              <button onClick={handleLogout}
                style={{ background:"#450a0a", border:"1px solid #7f1d1d", color:"#f87171", borderRadius:7, padding:"4px 8px", fontSize:9, fontWeight:700, cursor:"pointer" }}>
                OUT
              </button>
            </div>
            <div style={{ display:"flex", gap:5, marginTop:6 }}>
              <button onClick={() => setModal("profile")}
                style={{ flex:1, background:"#0a1628", border:"1px solid #1e3a2e", color:"#94a3b8", borderRadius:6, padding:"4px 0", fontSize:9, cursor:"pointer" }}>
                👤 Profile
              </button>
              <button onClick={() => setModal("password")}
                style={{ flex:1, background:"#0a1628", border:"1px solid #1e3a2e", color:"#94a3b8", borderRadius:6, padding:"4px 0", fontSize:9, cursor:"pointer" }}>
                🔒 Password
              </button>
              {isAdmin && (
                <button onClick={() => navigate("/collect")}
                  style={{ flex:1, background:"#0a1628", border:"1px solid #22c55e", color:"#4ade80", borderRadius:6, padding:"4px 0", fontSize:9, cursor:"pointer" }}>
                  ➕ Pole
                </button>
              )}
            </div>
            {isAdmin && (
              <button onClick={() => navigate("/admin")}
                style={{ marginTop:5, width:"100%", background:"linear-gradient(135deg,#1e1b4b,#312e81)",
                  border:"1px solid #4f46e5", color:"#a5b4fc", borderRadius:7, padding:"5px 0", fontSize:9, fontWeight:700, cursor:"pointer" }}>
                ⚙️ Admin Dashboard
              </button>
            )}
          </div>

          {nearCount > 0 && (
            <div className="fade-in" style={{ marginTop:7, background:"#14532d", border:"1px solid #22c55e",
              borderRadius:8, padding:"4px 10px", fontSize:10, color:"#86efac", fontWeight:700 }}>
              🔔 {nearCount} POLE{nearCount > 1 ? "S" : ""} WITHIN RANGE
            </div>
          )}
        </div>

        {/* GPS Status */}
        <div style={{ padding:"6px 14px", borderBottom:"1px solid #1a2e1a", fontSize:10, flexShrink:0 }}>
          {gpsError
            ? <span style={{ color:"#f87171" }}>❌ {gpsError}</span>
            : userPos
              ? <div>
                  <span style={{ color:"#34d399" }}>📡 {userPos.lat.toFixed(5)}, {userPos.lng.toFixed(5)}</span>
                  {accuracy != null && (
                    <span style={{ marginLeft:6, color: accuracy<15?"#4ade80":accuracy<50?"#fbbf24":"#f87171" }}>
                      ±{accuracy.toFixed(0)}m
                    </span>
                  )}
                </div>
              : <span style={{ color:"#6b7280" }}>📡 Acquiring GPS...</span>
          }
        </div>

        {/* Pole list */}
        <div style={{ flex:1, overflowY:"auto", padding:"8px 10px" }}>
          {poles.map(pole => (
            <PoleItem
              key={pole.id}
              pole={pole}
              isNear={!!nearPoles[pole.id]}
              dist={distances[pole.id]}
              blink={blinkOn && !!nearPoles[pole.id]}
              onSelect={() => setSelected(pole)}
              onEdit={() => navigate(`/collect?edit=${pole.id}`)}
              onDelete={() => { setDeleteTarget(pole); setModal("delete"); }}
              isAdmin={isAdmin}
            />
          ))}
        </div>

        {/* Legend + Nav */}
        <div style={{ padding:"8px 12px", borderTop:"1px solid #1a2e1a", fontSize:9, color:"#4b5563", flexShrink:0 }}>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"4px 10px", marginBottom:6 }}>
            {Object.entries(STATUS_COLORS).map(([s,c]) => (
              <div key={s} style={{ display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ width:7, height:7, borderRadius:"50%", background:c, display:"inline-block" }} />
                <span>{s}</span>
              </div>
            ))}
            <div style={{ display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ width:7, height:7, borderRadius:"50%", background:"#3b82f6", display:"inline-block" }} />
              <span>You</span>
            </div>
          </div>
          <div style={{ display:"flex", gap:5 }}>
            <button onClick={() => navigate("/dashboard")}
              style={{ flex:1, background:"#0a1628", border:"1px solid #1e3a2e", color:"#64748b", borderRadius:6, padding:"5px 0", fontSize:9, cursor:"pointer" }}>
              🏠 Home
            </button>
            <button onClick={() => navigate("/reports")}
              style={{ flex:1, background:"#0a1628", border:"1px solid #1e3a2e", color:"#64748b", borderRadius:6, padding:"5px 0", fontSize:9, cursor:"pointer" }}>
              📊 Reports
            </button>
          </div>
        </div>
      </div>

      {/* ══ MAP ══ */}
      <div style={{ flex:1, position:"relative", overflow:"hidden" }}>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={userPos || DEFAULT_CENTER}
            zoom={20}
            mapTypeId="satellite"
            onLoad={() => setMapReady(true)}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              gestureHandling: "greedy",
            }}
          >
            {mapReady && poles.map(pole => {
              const isNear = !!nearPoles[pole.id];
              const color  = STATUS_COLORS[pole.status] || "#22c55e";
              const pos    = { lat: parseFloat(pole.latitude), lng: parseFloat(pole.longitude) };
              return (
                <div key={pole.id}>
                  <Circle center={pos} radius={PROXIMITY_RADIUS} options={{
                    strokeColor:   isNear ? (blinkOn ? "#86efac" : "#4ade80") : color,
                    strokeOpacity: 1, strokeWeight: isNear ? 3 : 2,
                    fillColor:     isNear ? (blinkOn ? "#4ade80" : "#22c55e") : color,
                    fillOpacity:   isNear ? (blinkOn ? 0.7 : 0.2) : 0.25,
                  }} />
                  <Marker position={pos}
                    label={{ text: pole.pole_code, color: isNear?"#86efac":"#a3e635", fontSize:"10px", fontWeight:"bold" }}
                    icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: isNear?10:7,
                      fillColor: color, fillOpacity:1, strokeColor:"#fff", strokeWeight:2 }}
                    onClick={() => setSelected(pole)} />
                  {selectedPole?.id === pole.id && (
                    <InfoWindow position={pos} onCloseClick={() => setSelected(null)}>
                      <div style={{ fontFamily:"monospace", maxWidth:180 }}>
                        <strong style={{ color:"#166534" }}>{pole.name}</strong>
                        <p style={{ margin:"4px 0", fontSize:11 }}>{pole.description}</p>
                        <p style={{ margin:0, fontSize:10, color:"#6b7280" }}>
                          {pole.panel_watt}W · {pole.road_side} · {pole.status}
                          {pole.pole_type_label && ` · ${pole.pole_type_icon || ""} ${pole.pole_type_label}`}
                        </p>
                        {distances[pole.id] != null && (
                          <p style={{ margin:"4px 0 0", fontSize:11, color: isNear?"#16a34a":"#6b7280", fontWeight: isNear?700:400 }}>
                            {isNear ? `✅ ${distances[pole.id].toFixed(1)}m — In range` : `${distances[pole.id].toFixed(0)}m away`}
                          </p>
                        )}
                      </div>
                    </InfoWindow>
                  )}
                </div>
              );
            })}
            {mapReady && userPos && (
              <Marker position={userPos}
                icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale:10,
                  fillColor:"#3b82f6", fillOpacity:1, strokeColor:"#ffffff", strokeWeight:2 }}
                zIndex={999} title="You are here" />
            )}
          </GoogleMap>
        ) : (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", flexDirection:"column", gap:10 }}>
            <div style={{ width:36, height:36, border:"3px solid #22c55e", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 1s linear infinite" }} />
            <div style={{ color:"#4b5563", fontSize:12 }}>Loading map...</div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}
      </div>

      {/* ══ MODALS — only render when needed ══ */}
      {modal === "profile"  && <ProfileModal  user={currentUser} onClose={() => setModal(null)} onChangePassword={() => setModal("password")} />}
      {modal === "password" && <PasswordModal onClose={() => setModal(null)} />}
      {modal === "delete"   && deleteTarget && <DeleteModal pole={deleteTarget} onClose={() => { setModal(null); setDeleteTarget(null); }} onConfirm={handleDelete} />}
    </div>
  );
}
